import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { UserCog, Clock, Briefcase, AlertOctagon, ArrowRight } from 'lucide-react';
import logger from '@/lib/logger';
import { formatIsoBrt } from '@/lib/dateBrt';

const STATUS_LABEL = {
  not_started: 'Não iniciada',
  in_progress: 'Em andamento',
  pending: 'Pendente',
};
const PRIORITY_LABEL = { low: 'Baixa', medium: 'Média', high: 'Alta' };

function brDate(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10).split('-');
  if (s.length !== 3) return iso;
  return `${s[2]}/${s[1]}/${s[0]}`;
}

function addDaysISO(iso, days) {
  const base = iso ? new Date(iso) : new Date();
  base.setDate(base.getDate() + days);
  return formatIsoBrt(base);
}

function OccupationCellDrawer({ open, onOpenChange, consultant, projectId, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionTask, setActionTask] = useState(null);   // task being edited
  const [actionType, setActionType] = useState(null);   // 'reassign' | 'postpone'
  const [reassignTo, setReassignTo] = useState('');
  const [newDeadline, setNewDeadline] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!consultant || !projectId) return;
    setLoading(true);
    try {
      const res = await api.get('/occupation/cell', { params: { consultant, project_id: projectId } });
      setData(res.data);
    } catch (e) {
      logger.error('Error fetching cell tasks:', e);
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  }, [consultant, projectId]);

  useEffect(() => {
    if (open) {
      fetchTasks();
      setActionTask(null);
      setActionType(null);
    }
  }, [open, fetchTasks]);

  const startReassign = (task) => {
    setActionTask(task);
    setActionType('reassign');
    setReassignTo('');
  };
  const startPostpone = (task) => {
    setActionTask(task);
    setActionType('postpone');
    // Default: today + 7 days, or current deadline + 7 days
    setNewDeadline(addDaysISO(task.deadline, 7));
  };

  const cancelAction = () => {
    setActionTask(null);
    setActionType(null);
  };

  const handleSave = async () => {
    if (!actionTask) return;
    if (actionType === 'reassign' && !reassignTo.trim()) {
      toast.error('Informe o novo responsável');
      return;
    }
    if (actionType === 'postpone' && !newDeadline) {
      toast.error('Informe o novo prazo');
      return;
    }
    setSaving(true);
    try {
      const payload =
        actionType === 'reassign'
          ? { assigned_to: reassignTo.trim() }
          : { deadline: newDeadline };
      await api.put(`/tasks/${actionTask.task_id}`, payload);
      toast.success(
        actionType === 'reassign'
          ? `Tarefa reatribuída para ${reassignTo.trim()}`
          : `Prazo atualizado para ${brDate(newDeadline)}`
      );
      cancelAction();
      await fetchTasks();
      if (onChange) onChange();
    } catch (e) {
      logger.error('Save action error:', e);
      toast.error('Erro ao salvar alteração');
    } finally {
      setSaving(false);
    }
  };

  const tasks = data?.tasks || [];
  const project = data?.project || {};
  const pool = data?.consultants_pool || [];

  // Pre-compute the reassignment options once per (pool, current consultant).
  // Without this, the .filter ran for every render of every task row's
  // expanded editor — multiplied by N tasks in the drawer.
  const reassignOptions = React.useMemo(
    () => pool.filter((c) => c !== consultant),
    [pool, consultant]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-emerald-700" />
            {consultant}
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <Briefcase className="w-5 h-5 text-blue-700" />
            <span className="truncate">{project.name || projectId}</span>
          </DialogTitle>
          <DialogDescription>
            {project.project_number && <Badge variant="outline" className="mr-2">{project.project_number}</Badge>}
            {tasks.length} tarefa(s) ativa(s) · Clique nos botões à direita para reatribuir ou adiar prazo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2" data-testid="occupation-drawer-tasks">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-800"></div>
            </div>
          ) : tasks.length === 0 ? (
            <p className="text-center text-gray-500 py-12">Nenhuma tarefa ativa nesta célula.</p>
          ) : (
            tasks.map((t) => (
              <Card
                key={t.task_id}
                className={`p-3 border ${t.is_overdue ? 'border-red-200 bg-red-50/40' : 'border-gray-200'}`}
                data-testid={`drawer-task-${t.task_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 break-words">{t.name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                      {t.phase_name && (
                        <Badge variant="outline" className="text-[10px]">{t.phase_name}</Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        {STATUS_LABEL[t.status] || t.status}
                      </Badge>
                      {t.priority && (
                        <Badge variant="outline" className="text-[10px]">
                          {PRIORITY_LABEL[t.priority] || t.priority}
                        </Badge>
                      )}
                      <span className={`inline-flex items-center gap-1 ${t.is_overdue ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>
                        {t.is_overdue && <AlertOctagon className="w-3 h-3" />}
                        <Clock className="w-3 h-3" />
                        {brDate(t.deadline)}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startReassign(t)}
                      data-testid={`drawer-reassign-${t.task_id}`}
                    >
                      Reatribuir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startPostpone(t)}
                      data-testid={`drawer-postpone-${t.task_id}`}
                    >
                      Adiar
                    </Button>
                  </div>
                </div>

                {/* Inline action editor */}
                {actionTask?.task_id === t.task_id && (
                  <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                    {actionType === 'reassign' ? (
                      <div className="space-y-2">
                        <Label className="text-xs">Novo responsável</Label>
                        <Select value={reassignTo} onValueChange={setReassignTo}>
                          <SelectTrigger className="w-full" data-testid={`drawer-reassign-select-${t.task_id}`}>
                            <SelectValue placeholder="Selecione um consultor existente…" />
                          </SelectTrigger>
                          <SelectContent>
                            {reassignOptions.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="…ou digite um novo nome"
                          value={reassignTo}
                          onChange={(e) => setReassignTo(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label className="text-xs">Novo prazo</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="date"
                            value={newDeadline}
                            onChange={(e) => setNewDeadline(e.target.value)}
                            className="text-sm"
                            data-testid={`drawer-postpone-input-${t.task_id}`}
                          />
                          <Button size="sm" variant="ghost" onClick={() => setNewDeadline(addDaysISO(t.deadline, 7))}>
                            +7d
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setNewDeadline(addDaysISO(t.deadline, 14))}>
                            +14d
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setNewDeadline(addDaysISO(t.deadline, 30))}>
                            +30d
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 mt-3">
                      <Button size="sm" variant="ghost" onClick={cancelAction} disabled={saving}>
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-700 hover:bg-emerald-800"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid={`drawer-save-${t.task_id}`}
                      >
                        {saving ? 'Salvando…' : 'Salvar'}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OccupationCellDrawer;
